import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { globalHref } from '@/routing/tenant';
import { translateApiError } from '@/lib/apiError';
import { resetPassword, resetRejectionOf } from '@/features/auth/passwordReset';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/synapse/Field';
import { PasswordInput } from '@/components/synapse/PasswordInput';
import { PwStrength } from '@/components/synapse/PwStrength';
import { PolicyLine } from '@/components/synapse/PolicyLine';
import { AuthScreen } from '@/layouts/AuthScreen';
import { BrandPanel } from '@/components/synapse/BrandPanel';
import { passwordPolicy, passwordScore } from '@/lib/passwordScore';

/**
 * Choose a new password, holding a token from an email.
 *
 * The token is read from the query string and never rendered, logged, or put
 * anywhere it could be shoulder-surfed — it is the entire credential for this
 * account until it is spent.
 *
 * Nothing is validated client-side beyond the password rules. A "check the token
 * looks right" pre-flight would only teach the page to guess at answers the
 * server already gives precisely, and a token that LOOKS fine can still be
 * expired or spent — so the page has to handle that case regardless, and then
 * the pre-flight is a second code path that never earns its keep.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set when the link itself is dead, which is terminal — the form is retired. */
  const [linkDead, setLinkDead] = useState(false);

  const score = useMemo(() => passwordScore(password), [password]);
  const policy = useMemo(() => passwordPolicy(password), [password]);
  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;

  const canSubmit = password.length >= 8 && confirm === password && !submitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      // Strip the token from the address bar the moment it is spent.
      //
      // It is already burned server-side, so this is not what stops a replay —
      // it stops the string LINGERING: in browser history (which syncs across
      // devices on a signed-in profile), in a screenshot of the finished page,
      // and in the `Referer` of any later cross-origin navigation from here.
      // `replaceState` rather than a navigate, so the page keeps its state and
      // the user cannot reach the pre-reset URL with the back button.
      window.history.replaceState(null, '', window.location.pathname);
      setDone(true);
    } catch (err) {
      const rejection = resetRejectionOf(err);
      if (rejection) {
        // A dead link cannot be fixed by trying again, so the form goes away and
        // the only offer is a fresh link. Leaving the inputs up would invite the
        // user to retype a password five times against a token that is gone.
        setLinkDead(true);
        setError(t(`auth.resetRejection.${rejection}` as const));
      } else {
        setError(translateApiError(err, { fallback: t('auth.resetFailed') }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const brand = (
    <BrandPanel
      heading={
        <>
          {t('auth.brand.forgotHeadingLine1')}
          <br />
          {t('auth.brand.forgotHeadingLine2')}
        </>
      }
      body={t('auth.brand.forgotBody')}
      footer={
        <div className="flex w-full justify-between">
          <span>{t('common.appNameLong')}</span>
          <span className="mono">{t('common.version')}</span>
        </div>
      }
    />
  );

  // Arriving with no token at all — a truncated link, or someone typing the path
  // directly. Treated exactly like a dead one rather than showing a form that
  // cannot possibly succeed.
  const unusable = linkDead || token.length === 0;

  return (
    <AuthScreen brand={brand}>
      <div className="flex flex-col gap-7">
        <div>
          <h1 className="text-[26px] font-semibold leading-[1.1] tracking-[-0.025em] md:text-[22px]">
            {done ? t('auth.passwordResetDone') : t('auth.chooseNewPassword')}
          </h1>
          <p className="mt-2 text-sm leading-[1.45] text-muted md:text-[13px]">
            {done
              ? t('auth.passwordResetDoneBody')
              : unusable
                ? t('auth.resetLinkUnusableBody')
                : t('auth.chooseNewPasswordBody')}
          </p>
        </div>

        {!done && unusable && (
          <div className="text-[12.5px] text-destructive" role="alert">
            {error ?? t('auth.resetRejection.invalid')}
          </div>
        )}

        {!done && !unusable && (
          <form onSubmit={onSubmit} className="flex flex-col gap-3.5">
            <Field
              label={t('auth.newPassword')}
              error={tooShort ? t('auth.atLeast8Error') : undefined}
            >
              {(id) => (
                <div>
                  <PasswordInput
                    id={id}
                    required
                    inputSize="lg"
                    autoComplete="new-password"
                    placeholder={t('auth.atLeast8')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {password.length > 0 && (
                    <div className="mt-2 flex flex-col gap-2">
                      <PwStrength score={score} />
                      <div className="flex flex-col gap-1">
                        {policy.map((p) => (
                          <PolicyLine key={p.labelKey} ok={p.ok} label={t(p.labelKey)} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Field>

            <Field
              label={t('auth.confirmPassword')}
              error={mismatch ? t('auth.passwordsDoNotMatch') : undefined}
            >
              <PasswordInput
                required
                inputSize="lg"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </Field>

            {error && (
              <div className="text-[12.5px] text-destructive" role="alert">
                {error}
              </div>
            )}

            <Button type="submit" size="lg" full disabled={!canSubmit}>
              {submitting ? t('auth.resettingPassword') : t('auth.resetPassword')}
            </Button>
          </form>
        )}

        <div className="text-center text-[13px] text-muted">
          {/* Plain anchors for the same reason LoginPage uses one: these pages are
              global but render inside a clinic's basename too. */}
          {done ? (
            <a href={globalHref('/login')} className="text-ink underline underline-offset-[3px]">
              {t('auth.signIn')}
            </a>
          ) : (
            <a
              href={globalHref('/forgot-password')}
              className="text-ink underline underline-offset-[3px]"
            >
              {t('auth.requestNewResetLink')}
            </a>
          )}
        </div>
      </div>
    </AuthScreen>
  );
}
